import _, { entries, last, range } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import BaseModal from '../../../components/BaseModal/BaseModal';
import Loader from '../../../components/Loader/Loader';
import Modal from '../../../components/Modal/Modal';
import PurchaseReturnForm from '../../../components/PurchaseReturnForm/PurchaseReturnForm';
import InitTable, { Query } from '../../../components/Table/Table';
import ToastMessage, {
  ToastMessageRefType,
} from '../../../components/ToastMessage/ToastMessage';
import { PurchaseReturn, PurchaseReturnResponse, PurchaseReturnUI } from '../../../interface';
import {
  AsyncStatus,
  getPurchasedReturnList,
  updatePurchasedReturnToStore,
} from '../../../redux/features/admin-slice';
import { RootState, useAppDispatch } from '../../../redux/store';
import {
  deletePurchasedReturns,
  restorePurchasedReturns,
  getErrorMessage,
  getPurchasedReturn,
  getSystemTaxYears,
  updatePurchasedReturns,
} from '../../../services';
import style from './PurchasedReturn.module.scss';
import moment from 'moment';
import classNames from 'classnames';
import TableMenu, { MenuItem } from '../../../components/TableMenu/TableMenu';
import { TOOLTIP_SECTIONS } from '../../../enums';
import { formatDate } from '../../../utils/stringUtil';

const { Table, Column } = InitTable<PurchaseReturnData>();

interface PurchaseReturnData {
  id?: number;
  fullName: string;
  freeTrialStartDate: string;
  businessName: string;
  years: Map<number, number>;
  status?: string;
  latestContractStartDate?: string;
  latestContractEndDate?: string;
}

interface Props {
  purchasedReturn: PurchaseReturnResponse;
  fetchStatus?: AsyncStatus;
}

function PurchasedReturn(props: Props) {
  const { purchasedReturn, fetchStatus } = props;
  const toastRef = useRef<ToastMessageRefType | null>(null);

  const dashboardDispatch = useAppDispatch();

  // Search input field
  const currentCalendarYear = moment().year();
  const [fetchingAllTaxYears, setFetchingAllTaxYears] = useState(false);
  const [currentTaxYear, setCurrentTaxYear] = useState<number>(currentCalendarYear);
  const [toBeDeleted, setToBeDeleted] = useState<PurchaseReturnData>();
  const [toBeRestored, setToBeRestored] = useState<PurchaseReturnData>();
  const [editableData, setEditableData] = useState<PurchaseReturnUI>();
  const [query, setQuery] = useState<Query>();

  // Component initialization, fetch system tax years
  useEffect(() => {
    setFetchingAllTaxYears(true);
    getSystemTaxYears()
      .then((res) => {
        setFetchingAllTaxYears(false);
        const allActiveTaxYears = _.orderBy(
          res.data,
          ['taxYear'],
          ['desc'],
        );

        let currentTaxYearIndex = _.findIndex(allActiveTaxYears, {
          isCurrent: true,
        });
        if (currentTaxYearIndex < 0) {
          currentTaxYearIndex = 0;
        }
        setCurrentTaxYear(allActiveTaxYears[currentTaxYearIndex].taxYear);
      })
      .catch(() => {
        setFetchingAllTaxYears(false);
      });
  }, []);

  const handleDelete = (value: PurchaseReturnData) => {
    setToBeDeleted(value);
  };
  const handleRestore = (value: PurchaseReturnData) => {
    setToBeRestored(value);
  };

  const constructPurchaseData = (arr: PurchaseReturn[]) => {
    const res = arr.map((item: PurchaseReturn) => {
      const sortedContracts = _.orderBy(
        item.userPurchases,
        [(contract) => contract.contractStartDate ? new Date(contract.contractStartDate).getTime() : Number.MAX_SAFE_INTEGER],
        ['desc']
      );

      const latestContract = sortedContracts.length > 0 ? sortedContracts[0] : null;

      return {
        id: item.id,
        fullName: `${item.firstName} ${item.lastName}`,
        freeTrialStartDate: `${item.freeTrialStartDate ? item.freeTrialStartDate : ''}${item.freeTrialStartDate ? ' - ' : ''
          }${item.freeTrialEndDate ? item.freeTrialEndDate : ''}`,
        businessName: item.businessName,
        years: new Map(
          item.userPurchases.map((y) => [y.taxYear, y.returnsPurchased]),
        ),
        status: item.status,
        latestContractStartDate: latestContract?.contractStartDate || '',
        latestContractEndDate: latestContract?.contractEndDate || ''
      } as PurchaseReturnData;
    });

    return res;
  };

  const queryPurchasedReturnList = (queryVal: Query | undefined) => {
    if (queryVal) {
      const sort = last(entries(queryVal?.order));

      dashboardDispatch(
        getPurchasedReturnList({
          page: queryVal.pagination?.page ?? 1,
          perPage: queryVal.pagination?.pageSize ?? 10,
          sortBy: sort ? sort[0] : '',
          sortDirection: sort ? sort[1] : '',
          text: queryVal.search ?? '',
        }),
      );
    }
  };

  useEffect(() => {
    queryPurchasedReturnList(query)
  }, [query]);

  const data = useMemo(
    () => constructPurchaseData(purchasedReturn.items),
    [purchasedReturn, query?.search, query?.filter, query?.order],
  );
  const [submitting, setSubmitting] = useState(false);
  const [updateErrorMessage, setUpdateErrorMessage] = useState('');

  const handleSubmit = (entry: PurchaseReturnUI) => {
    setSubmitting(true);
    setUpdateErrorMessage('');
    updatePurchasedReturns(entry.id || 0, {
      businessName: entry.businessName,
      email: entry.email,
      firstName: entry.firstName,
      lastName: entry.lastName,
      freeTrialStartDate: entry.freeTrialStartDate,
      freeTrialEndDate: entry.freeTrialEndDate,
      setupCompleted: entry.setupCompleted,
      userPurchases: entry.userPurchases.map((item) => ({
        id: item.id,
        taxYear: item.taxYear,
        contractStartDate: item.contractStartDate,
        contractEndDate: item.contractEndDate,
        returnsPurchased: item.returnsPurchased,
      })),
      roles: entry.roles
    })
      .then((resp) => {
        const errorMessage = getErrorMessage(resp);
        if (errorMessage) {
          toastRef.current?.showErrorToast(errorMessage);
        } else {
          toastRef.current?.showSuccessToast('Update successfully');
          dashboardDispatch(updatePurchasedReturnToStore(resp.data));
          if (purchasedReturn.items.length > 1) {
            queryPurchasedReturnList(query);
          }
        }
        setSubmitting(false);
        setEditableData(undefined);
      })
      .catch((err) => {
        setSubmitting(false);
        const errMessage = getErrorMessage(err);
        if (errMessage) {
          setUpdateErrorMessage(errMessage);
        } else {
          setEditableData(undefined);
        }
      });
  };

  const [impersonateModalOpen, setImpersonateModalOpen] = useState<boolean>(false);
  const [impersonateId, setImpersonateId] = useState<PurchaseReturnData>();
  const impersonate = (item: PurchaseReturnData) => {
    setImpersonateModalOpen(true);
    setImpersonateId(item);
  };

  const keepImpersonate = () => {
    if (impersonateId?.id) {
      sessionStorage.setItem('user-account-id', _.toString(impersonateId.id));
      sessionStorage.removeItem('skip');
      window.location.href = '/dashboard';
    }
  };

  const closeImpersonateComfirm = () => {
    setImpersonateModalOpen(false);
    setImpersonateId(undefined);
  };

  function getMenuItems(item: PurchaseReturnData) {
    const actions = [_.assign({},
      item.status === 'Active'
      && {
        Edit: async () => {
          const editable = purchasedReturn.items.find(
            (x) => x.id === item?.id,
          );
          const res = await getPurchasedReturn(item?.id as number);
          if (editable) {
            const sortedData = {
              ...editable,
              roles: res.data.roles,
              userPurchases: _.orderBy(
                res.data.userPurchases || editable.userPurchases,
                [(item) => item.contractStartDate ? new Date(item.contractStartDate).getTime() : Number.MAX_SAFE_INTEGER],
                ['asc']
              )
            };
            setEditableData(sortedData);
          }
        },
      },
      item.status === 'Active'
      && {
        Inactivate: () => {
          handleDelete(item);
        },
      },
      item.status === 'Inactive'
      && {
        Reactivate: () => {
          handleRestore(item);
        },
      },
      item.status === 'Active'
      && {
        Impersonate: () => {
          impersonate(item);
        },
      }
    )
    ];

    return [
      _.map(
        _.assign({}, ..._.compact(actions)),
        (onClick, label) =>
        ({
          variant: 'default',
          tooltip: {
            section: TOOLTIP_SECTIONS.TableRowAction,
            keyword: label,
          },
          label,
          onClick,
        } as MenuItem)
      ),
    ];
  }

  return (
    <>
      <div className={style['main-content']}>
        {/* Braedcrumb */}
        <h3>Customer Accounts</h3>
        <ToastMessage ref={toastRef} className={style['toast']} />

        <Table
          id='purchasedReturns'
          rows={data}
          onQueryChanged={setQuery}
          paginate
          searchable='Search purchased returns...'
          totalRows={purchasedReturn.total}
          loading={false}
        >
          <Column label='Customer Admin Name' prop='fullName' sortable />
          <Column label='Customer Account Name' prop='businessName' sortable />
          <Column
            id='contractStartDate'
            label='Contract Start'
          >
            {(item: PurchaseReturnData) => formatDate(item.latestContractStartDate)}
          </Column>
          <Column
            id='contractEndDate'
            label='Contract End'
          >
            {(item: PurchaseReturnData) => formatDate(item.latestContractEndDate)}
          </Column>
          <Column
            key={currentTaxYear}
            id={'taxYear' + currentTaxYear}
            label='Returns Purchased'
            accessor={(item) => item.years.get(currentTaxYear) ?? null}
            sortable
          />
          <Column
            id='status'
            label='Status'
            // filterable
            sortable={false}
          >
            {(item: PurchaseReturnData) => (
              <span
                className={classNames(
                  'badge-status',
                  item?.status === 'Active' ? 'active' : 'inactive'
                )}
              >
                {item?.status}
              </span>
            )}
          </Column>

          {/* Actions column  */}
          <Column id='actions' label='Actions' sortable={false}>
            {(item: PurchaseReturnData) => (
              <div className={style['actions-column-cell']}>
                <TableMenu
                  toggle={
                    <button type='button'>
                      <i className={style['icon-3-dots']} />
                    </button>
                  }
                  menuItems={getMenuItems(item)}
                />
              </div>
            )}
          </Column>
        </Table>
      </div>

      <Modal
        title='Confirmation'
        body={<p>Are you sure you want to inactivate the Customer Account?</p>}
        isOpen={!!toBeDeleted}
        onClose={() => setToBeDeleted(undefined)}
        footer={
          <div className='buttons'>
            <button
              className='primary-button'
              onClick={() => {
                const item = purchasedReturn.items.find(
                  (x) => x.id === toBeDeleted?.id,
                );
                if (item && item.id) {
                  deletePurchasedReturns(item.id)
                    .then((e) => {
                      const errorMessage = getErrorMessage(e);
                      if (errorMessage) {
                        toastRef.current?.showErrorToast(errorMessage);
                      } else {
                        toastRef.current?.showSuccessToast('Customer Account inactivated successfully');
                      }
                      if (purchasedReturn.items.length > 1) {
                        queryPurchasedReturnList(query);
                      }
                    })
                    .catch((err) => {
                      toastRef.current?.showErrorToast(
                        err?.data?.message ??
                        `An error occurred, please try again later.`,
                      );
                    });
                }
                setToBeDeleted(undefined);
              }}
            >
              Confirm
            </button>
            <button
              className='default-button'
              onClick={() => setToBeDeleted(undefined)}
            >
              Cancel
            </button>
          </div>
        }
      />
      <Modal
        title='Confirmation'
        body={<p>Are you sure you want to reactivate the Customer Account?</p>}
        isOpen={!!toBeRestored}
        onClose={() => setToBeRestored(undefined)}
        footer={
          <div className='buttons'>
            <button
              className='primary-button'
              onClick={() => {
                const item = purchasedReturn.items.find(
                  (x) => x.id === toBeRestored?.id,
                );
                if (item && item.id) {
                  restorePurchasedReturns(item.id)
                    .then((e) => {
                      const errorMessage = getErrorMessage(e);
                      if (errorMessage) {
                        toastRef.current?.showErrorToast(errorMessage);
                      } else {
                        toastRef.current?.showSuccessToast('Customer Account reactivated successfully');
                      }
                      if (purchasedReturn.items.length > 1) {
                        queryPurchasedReturnList(query);
                      }
                    })
                    .catch((err) => {
                      toastRef.current?.showErrorToast(
                        err?.data?.message ??
                        `An error occurred, please try again later.`,
                      );
                    });
                }
                setToBeRestored(undefined);
              }}
            >
              Confirm
            </button>
            <button
              className='default-button'
              onClick={() => setToBeRestored(undefined)}
            >
              Cancel
            </button>
          </div>
        }
      />
      <BaseModal isOpen={!!editableData} className={style['modal']}>
        <div className={style['modal-wrapper']}>
          <h3>Edit User</h3>
          {editableData && (
            <PurchaseReturnForm
              inEditMode
              data={editableData}
              onSubmit={handleSubmit}
              submitting={submitting}
              sectionClassName={style['section']}
              formClassName={style['form']}
              onClear={() => {
                setEditableData(undefined);
              }}
              innerModal={true}
              errorMessage={updateErrorMessage}
            />
          )}
        </div>
      </BaseModal>

      <Modal
        className='impersonate-modal'
        title='Confirmation'
        body={
          <p>
            Any changes made while using impersonate feature will be tracked as
            system admin action, do you want to keep continue?
          </p>
        }
        isOpen={impersonateModalOpen}
        footer={
          <div className='buttons'>
            <button className='primary' onClick={keepImpersonate}>
              Yes
            </button>
            <button
              className='secondary'
              onClick={closeImpersonateComfirm}
            >
              No
            </button>
          </div>
        }
      />

      <Loader isOpen={
        fetchStatus === 'pending' ||
        fetchingAllTaxYears
      } />
    </>
  );
}

const mapStateToProps = (state: RootState) => ({
  fetchStatus: state.admin?.fetchStatus,
  purchasedReturn: state.admin?.purchasedReturn,
});

export default connect(mapStateToProps)(PurchasedReturn);
